/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { getReactRef, type Ref } from '../getReactRef/getReactRef';
import { isDecoupledEditor } from '../isDecoupledEditor/isDecoupledEditor';
import {
	EDITOR_DECOUPLED_MENU_BAR_IDENTIFIER,
	EDITOR_DECOUPLED_TOOLBAR_IDENTIFIER,
	EDITOR_WORD_COUNT
} from '../../constants/identifiers';
import { CKBOX_VERSION, CKEDITOR5_VERSION, type EditorType } from '@ckeditor/ckeditor5-metadata';
import type { EditorPlugins } from '../../types/EditorPlugins';
import type { EditorLanguage } from '../../components/LanguageSelection/languages';
import type { InstallMethod, Technology } from '../../constants/installation';
import { groupByInstallPackage } from '../groupByInstallPackage/groupByInstallPackage';
import { areTranslationsNeeded, getTranslationImports } from '../translations/translations';
import type { ToolbarOptions } from '../../types/ToolbarsOptions';
import { getStylesImport } from '../getStylesImport/getStylesImport';
import { getPluginsImport, type PluginsImportResult } from '../getPluginsImport/getPluginsImport';
import { getUsersIntegrationPlugin } from '../getUsersIntegrationPlugin/getUsersIntegrationPlugin';
import { getCommentsIntegrationPlugin } from '../getCommentsIntegrationPlugin/getCommentsIntegrationPlugin';
import {
	getRevisionHistoryIntegrationPlugin
} from '../getRevisionHistoryIntegrationPlugin/getRevisionHistoryIntegrationPlugin';
import { getTrackChangesIntegrationPlugin } from '../getTrackChangesIntegrationPlugin/getTrackChangesIntegrationPlugin';
import { getConfigUpdateAlert } from '../getConfigUpdateAlert/getConfigUpdateAlert';
import { getTokenVariables } from '../getTokenVariables/getTokenVariables';
import type { CustomerPortalAccount } from '../../contexts/CustomerPortalContext';
import type { EditorOptions } from '../../components/SnippetsContainer/Snippets/types';
import { getFilename } from '../getFilename/getFilename';
import { requiresWordCountPlugin } from '../requiresWordCountPlugin/requiresWordCountPlugin';
import { getBuilderUrlComment } from '../getBuilderUrlComment/getBuilderUrlComment';
import { getDefaultHexColors } from '../getDefaultHexColors/getDefaultHexColors';

export type GetReactSourceTemplateArgs = {
	customerPortalAccount: CustomerPortalAccount | null;
	editorConfig: string;
	editorOptions: EditorOptions;
	evaluationLicense: string;
	htmlString: string;
	refs: Array<Ref>;
	selectedInstallMethod: InstallMethod;
	selectedTechnology: Technology;
	builderUrl: string;
};

export function getReactSourceTemplate( {
	customerPortalAccount,
	editorConfig,
	editorOptions,
	evaluationLicense,
	htmlString,
	refs,
	selectedInstallMethod,
	selectedTechnology,
	builderUrl
}: GetReactSourceTemplateArgs ): string {
	const { editorType, toolbarOptions, editorPlugins, editorLanguage } = editorOptions;

	const cssFilename = getFilename( { type: 'css', selectedTechnology } );
	const groupedPlugins = groupByInstallPackage( editorPlugins );
	const hasPremiumPlugins = groupedPlugins[ 'ckeditor5-premium-features' ].length > 0;
	const configUpdateAlert = getConfigUpdateAlert( { editorPlugins, editorConfig, configRef: 'editorConfig' } );
	const pluginsImport = getPluginsImport( editorType, groupedPlugins );
	const returnValue = htmlString.replace(
		'__REACT_COMPONENT_PLACEHOLDER__',
		getReactComponent( editorType, toolbarOptions, selectedInstallMethod, editorPlugins )
	);

	return `
		${ getBuilderUrlComment( builderUrl ) }

		${ getReactImports() }
		${ getCKEditorIntegrationImports( selectedInstallMethod ) }
		${ selectedInstallMethod !== 'cdn' ? getCKEditorImports( pluginsImport, selectedInstallMethod ) : '' }

		${ selectedInstallMethod !== 'cdn' ? getTranslationImports( editorLanguage, hasPremiumPlugins ) : '' }

		${ getStylesImport( selectedInstallMethod, hasPremiumPlugins ) }

		import './${ cssFilename }';

		${ getTokenVariables( { editorConfig, editorPlugins, customerPortalAccount, evaluationLicense, selectedInstallMethod } ) }

		${ getDefaultHexColors( editorPlugins ) }

		${ selectedInstallMethod !== 'cdn' ? getIntegrationPlugins( editorPlugins ) : '' }

		export default function App() {
			${ getRefs( refs ) }
			const [ isLayoutReady, setIsLayoutReady ] = useState( false );
			${ selectedInstallMethod === 'cdn' ? getCKEditorCloudComposable( editorLanguage, editorPlugins, hasPremiumPlugins ) : '' }

			useEffect( () => {
				setIsLayoutReady( true );

				return () => setIsLayoutReady( false );
			}, [] );

			${ getEditorConfig( selectedInstallMethod, pluginsImport, editorPlugins, editorType, editorConfig ) }

			${ wrapConfigUpdateAlertCall( configUpdateAlert.call ) }

			return (
				${ returnValue }
			)
		}

		${ configUpdateAlert.fn }
	`;
}

function getReactImports() {
	const imports = [
		'useState',
		'useEffect',
		'useRef',
		'useMemo'
	];

	return `import { ${ imports.join( ', ' ) } } from 'react';`;
}

function getCKEditorIntegrationImports( selectedInstallMethod: InstallMethod ) {
	const imports = [
		'CKEditor'
	];

	if ( selectedInstallMethod === 'cdn' ) {
		imports.push( 'useCKEditorCloud' );
	}

	return `import { ${ imports } } from '@ckeditor/ckeditor5-react';`;
}

function wrapConfigUpdateAlertCall( configUpdateAlertCall: string ) {
	if ( !configUpdateAlertCall ) {
		return '';
	}

	return `
		useEffect( () => {
			if ( editorConfig ) {
				${ configUpdateAlertCall }
			}
		}, [ editorConfig ] );
	`;
}

function getCKEditorCloudComposable( editorLanguage: EditorLanguage, editorPlugins: EditorPlugins, hasPremiumPlugins: boolean ) {
	const cloudConfig: Record<string, any> = {
		version: CKEDITOR5_VERSION
	};

	if ( hasPremiumPlugins ) {
		cloudConfig.premium = true;
	}

	if ( areTranslationsNeeded( editorLanguage ) ) {
		cloudConfig.translations = [ editorLanguage ];
	}

	if ( editorPlugins.includes( 'CKBox' ) ) {
		cloudConfig.ckbox = {
			version: CKBOX_VERSION
		};
	}

	return `const cloud = useCKEditorCloud( ${ JSON.stringify( cloudConfig ) } );`;
}

function getCKEditorImports(
	{ importsFromCKEditor, importsFromCKEditorPremiumFeatures }: PluginsImportResult,
	selectedInstallMethod: InstallMethod
): string {
	const prefix = selectedInstallMethod === 'npm' ?
		'import' :
		'const';
	const ckeditor5Suffix = selectedInstallMethod === 'npm' ?
		'from \'ckeditor5\'' :
		'= cloud.CKEditor';
	const ckeditor5PremiumSuffix = selectedInstallMethod === 'npm' ?
		'from \'ckeditor5-premium-features\'' :
		'= cloud.CKEditorPremiumFeatures';

	const imports = [
		`${ prefix } { ${ importsFromCKEditor } } ${ ckeditor5Suffix };`
	];

	if ( importsFromCKEditorPremiumFeatures.length > 0 ) {
		imports.push(
			`${ prefix } { ${ importsFromCKEditorPremiumFeatures } } ${ ckeditor5PremiumSuffix };`
		);
	}

	return imports.join( '\n' );
}

function getEditorConfig(
	selectedInstallMethod: InstallMethod,
	pluginsImport: PluginsImportResult,
	editorPlugins: EditorPlugins,
	editorType: EditorType,
	editorConfig: string
) {
	const isCdn = selectedInstallMethod === 'cdn';

	const destructuring = isCdn ? `${ editorType }, editorConfig` : 'editorConfig';
	const guardClause = isCdn ? 'cloud.status !== \'success\' || !isLayoutReady' : '!isLayoutReady';
	const returnValue = isCdn ? `${ editorType },\neditorConfig: ${ editorConfig }` : `editorConfig: ${ editorConfig }`;
	const dependencies = isCdn ? 'cloud, isLayoutReady' : 'isLayoutReady';

	return `
		const { ${ destructuring } } = useMemo( () => {
			if ( ${ guardClause } ) {
				return {};
			}

			${ isCdn ? getCKEditorImports( pluginsImport, selectedInstallMethod ) : '' }

			${ isCdn ? getIntegrationPlugins( editorPlugins ) : '' }

			return {
				${ returnValue }
			};
		}, [${ dependencies }] );
	`;
}

function getIntegrationPlugins( editorPlugins: EditorPlugins ) {
	return `
		${ getUsersIntegrationPlugin( editorPlugins ) }

		${ getCommentsIntegrationPlugin( editorPlugins ) }

		${ getTrackChangesIntegrationPlugin( editorPlugins ) }

		${ getRevisionHistoryIntegrationPlugin( editorPlugins ) }
	`;
}

function getRefs( refs: Array<Ref> ) {
	return refs
		.map( ( { getter } ) => `const ${ getter } = useRef( null );` )
		.join( '\n' );
}

function getReactComponent(
	editorType: EditorType,
	toolbarOptions: ToolbarOptions,
	selectedInstallMethod: InstallMethod,
	editorPlugins: EditorPlugins
) {
	const decoupledToolbarRef = getReactRef( EDITOR_DECOUPLED_TOOLBAR_IDENTIFIER ).current;
	const decoupledMenuBarRef = getReactRef( EDITOR_DECOUPLED_MENU_BAR_IDENTIFIER ).current;
	const wordCountRef = getReactRef( EDITOR_WORD_COUNT ).current;
	const isDecoupled = isDecoupledEditor( editorType );
	const hasWordCount = requiresWordCountPlugin( editorPlugins );

	const onReadyCallback = `
		onReady={ editor => {
			${ hasWordCount ? 'const wordCount = editor.plugins.get( \'WordCount\' );' : '' }
			${ hasWordCount ? `${ wordCountRef }.appendChild( wordCount.wordCountContainer );` : '' }
			${ isDecoupled ? `${ decoupledToolbarRef }.appendChild( editor.ui.view.toolbar.element );` : '' }
			${ toolbarOptions.isMenuBarUsed ? `${ decoupledMenuBarRef }.appendChild( editor.ui.view.menuBarView.element );` : '' }
		} }
	`;

	const onAfterDestroyCallback = `
		onAfterDestroy={ () => {
			${ hasWordCount ? `Array.from( ${ wordCountRef }.children ).forEach( child => child.remove() );` : '' }
			${ isDecoupled ? `Array.from( ${ decoupledToolbarRef }.children ).forEach( child => child.remove() );` : '' }
			${ toolbarOptions.isMenuBarUsed ? `Array.from( ${ decoupledMenuBarRef }.children ).forEach( child => child.remove() );` : '' }
		} }
	`;

	const editorStartupChecks = selectedInstallMethod === 'cdn' ?
		[ editorType, 'editorConfig' ] :
		[ 'editorConfig' ];

	return `
		{ ${ editorStartupChecks.join( ' && ' ) } && (
			<CKEditor
				${ isDecoupled || hasWordCount ? onReadyCallback : '' }
				${ isDecoupled || hasWordCount ? onAfterDestroyCallback : '' }
				editor={ ${ editorType } }
				config={ editorConfig }
			/>
		) }`;
}
